import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import * as projectClient from '../../models/projects/projectClient';
import Loader from '../common/loader';
import MobileSelector from './mobileSelector';
import ProjectsSelector from './projectsSelector';
import Card from './card';
import PrCard from './prCard';
import type { NewsPicksBannerProps } from '../common/newsPicksBanner';
import { useSearchInput } from '../../hooks/useSearchInput';

interface Props {
  keyword: string | null;
  order: string | null;
  speficInvestorFlg: boolean;
  newsPicksBanner: NewsPicksBannerProps | null;
}

// PRカードの差し込み位置（先頭から数えたカードのindex）。Figmaでは2段目の右=4枚目
const PR_CARD_INDEX = 3;

export default function IndexView({
  keyword: initialKeyword,
  order: initialOrder,
  speficInvestorFlg,
  newsPicksBanner,
}: Props) {
  const [order, setOrder] = useState<string | null>(initialOrder);
  // 検索や並び順を選ぶたびに generation を進めてキーを変え、一覧を空にして 1 ページ目から取り直す。
  // 同じ並び順をもう一度クリックしたときのように条件が変わらなくても取り直し、前に見た条件のキャッシュも使わない
  const [query, setQuery] = useState({
    keyword: initialKeyword ?? '',
    generation: 0,
  });

  // キーごとに結果を持つため、前の条件の応答が遅れて届いても今の一覧を上書きしない
  const { data, isPending, isFetching, isError, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ['projects', query.keyword, order, query.generation],
      queryFn: ({ pageParam, signal }) =>
        projectClient.fetchAll(
          pageParam,
          query.keyword === '' ? null : query.keyword,
          order,
          signal,
        ),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _pages, lastPageParam) =>
        lastPage.hasNext ? lastPageParam + 1 : undefined,
    });
  const projects = data?.pages.flatMap(page => page.items) ?? [];

  // PRカードの表示判定とリンク先。取得に失敗しても一覧表示には影響させない（カードを出さないだけ）
  const { data: pr = null } = useQuery({
    queryKey: ['projectsPr'],
    queryFn: ({ signal }) => projectClient.fetchPr(signal),
    select: response => response.pr,
    meta: { toast: false },
  });

  const startOver = (keyword: string) => {
    setQuery(current => ({ keyword, generation: current.generation + 1 }));
  };

  // 取得は確定した検索語でだけ走らせる。1 文字ごとの入力や IME の変換中には取得しない
  const search = useSearchInput({
    initialValue: initialKeyword ?? '',
    onCommit: startOver,
  });

  const onLoadMore = () => {
    // InfiniteScroll はマウント時にも呼ぶため、取得中は次のページを取らない
    if (!isFetching && hasNextPage) {
      void fetchNextPage();
    }
  };

  const onSubmitSearch = () => {
    // 検索アイコンと Enter は並び順を「全て」に戻す。並び順を選ぶとキーワードが消えるのと対になる挙動。
    // IME の変換中は submit が確定しないので、並び順だけ動かさないよう戻り値で分ける
    if (search.submit()) setOrder(null);
  };

  const onClickOrder = (orderValue: string | null) => {
    if (!isFetching) {
      // 並び順を選んだらキーワードを消す。控えていた自動検索もここで取り消される。
      // reset と startOver には同じ値を渡すこと。ずれると入力欄と query の keyword が食い違い、
      // hooks 側の「同じ語なら再取得しない」判定が次の自動検索で誤って発火する
      search.reset('');
      startOver('');
      setOrder(orderValue);
    }
  };

  const cards = projects.map(project => (
    <Card
      key={project.id}
      project={project}
      speficInvestorFlg={speficInvestorFlg}
    />
  ));
  if (pr && cards.length > 0) {
    cards.splice(
      Math.min(PR_CARD_INDEX, cards.length),
      0,
      <PrCard key="pr-card" pr={pr} />,
    );
  }

  return (
    <div>
      <div className="row">
        <div className="col d-block d-lg-none mb-10 justify-content-center">
          <MobileSelector
            order={order}
            searchInputProps={search.inputProps}
            onClickOrder={onClickOrder}
            onSubmitSearch={onSubmitSearch}
          />
        </div>
        <div className="col-lg-3 d-none d-lg-block">
          <ProjectsSelector
            order={order}
            newsPicksBanner={newsPicksBanner}
            searchInputProps={search.inputProps}
            onClickOrder={onClickOrder}
            onSubmitSearch={onSubmitSearch}
          />
        </div>
        <div className="col-lg-9 main-row__wrapper">
          <InfiniteScroll
            pageStart={0}
            loadMore={onLoadMore}
            // 1 ページ目の取得中もローディングを出す。失敗したら追加読み込みを止め、再取得を繰り返さない
            hasMore={isPending || (hasNextPage && !isError)}
            loader={<Loader key={0} type="water" />}
            className="row"
          >
            {cards}
          </InfiniteScroll>
          {!isFetching && projects.length === 0 && (
            <div>プロジェクトは見つかりませんでした</div>
          )}
        </div>
        <div className="col d-block d-lg-none">
          <div className="project-selector__attention">
            <span>※</span>
            <span>
              募集ページについては、募集期間終了後、募集の成立不成立に関わらず事業者の要請に応じて非開示となることがあります。ご了承下さい。
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
